<script>
  import { fly } from "svelte/transition";
  import { linear } from "svelte/easing";
  import { tick, getContext } from "svelte";
  import set from "lodash/set";
  import get from "lodash/get";
  import merge from "lodash/merge";
  import uniqBy from "lodash/uniqBy";
  import isEmpty from "lodash/isEmpty";
  import cloneDeep from "lodash/cloneDeep";
  import { loadImage } from "iso-canvas";
  import { DimText, Dimension, Quantity } from "dimtext";
  import { distSq, equal, subtract, add, midpoint, scale } from "vector";
  import { insertionIndex } from "overline";
  import {
    updateItemCache,
    createFreeShape as freeShape,
    gridlinePositions,
    positionsToLines,
    MergeStore,
  } from "@local/lamina-core";

  import WarningIcon from "@local/assets/icons/warning.svg";

  import Viewport from "#lib/drawing/Viewport.svelte";
  import DimtextInput from "#lib/drawing/DimtextInput.svelte";
  import TextInput from "#lib/drawing/TextInput.svelte";
  import SelectInput from "#lib/drawing/SelectInput.svelte";
  import SelectedActions from "./SelectedActions.svelte";

  import Polyface from "@local/extensions/geometry/polyface.js";
  import { findFlatIndex, findNestedIndex } from "@local/extensions/collections/find-flat-index.js";
  import dimSettings from "@local/extensions/utilities/dim-settings.js";
  import isRectangle from "@local/extensions/geometry/is-rectangle.js";
  import containedLocations from "#src/extensions/contained-locations.js";
  import { nearestPointOnSegment } from "@local/extensions/geometry/nearest-point-on-line.js";
  import {
    nearestEdgeOffsetReference,
    edgeReference,
    nearestEdgeReference,
  } from "@local/extensions/geometry/edge-reference.js";
  import polyfaceContainsPoint from "@local/extensions/geometry/polyface-contains-point.js";
  import { nearestCornerReference } from "@local/extensions/geometry/corner-reference.js";
  import { edgeOffsetCenter, edgePoint } from "@local/extensions/geometry/edge-offset-center.js";
  import liteDrawing from "@local/extensions/drawing/lite-drawing-detailed.js";
  import { isPlumb } from "@local/extensions/geometry/is-right-angle.js";
  import { roundValue } from "@local/extensions/parsers/round-value.js";
  import roundPt from "@local/extensions/parsers/round-point.js";
  import { snap, snapOrtho, snapEdge, snapEdgeOrtho, snapLinear } from "@local/extensions/geometry/snap.js";
  import { adjacentVertexConstrained } from "@local/extensions/geometry/adjacent-vertex-constrained.js";
  import { draggedClip } from "@local/extensions/drawing/clip-rectangle.js";
  import { drawingSrc as getDrawingSrc } from "#src/api";
  import { multiplier } from "@local/extensions/utilities/scales.js";
  import { slicePolygon } from "@local/extensions/geometry/slice-polygon.js";
  import { discontiguousKey } from "#src/extensions/event-keys.js";

  import {
    currentTool,
    tempFeatureHole,
    tempFeatureEdgeFabrication,
    tempFeatureCornerFabrication,
    tempFeatureEdgeAlignedFab,
    settingDisplayShowSpanningDims,
  } from "#src/stores/ui.js";

  export let paddingTop = 0;
  export let paddingRight = 0;
  export let paddingBottom = 0;
  export let paddingLeft = 0;
  export let disabled;
  export let isCollection = false;
  export let slide = "none";
  export let initialExtents = null;
  export let showDrawing = true;
  export let showActionButtons = false;

  // 2-way binding with parent component
  export let activeInput = null;
  export let hoveredFeatures = [];
  export let selectedFeatures = [];

  export let group;
  export let itemid;
  export let item;
  export let type;
  export let types;

  const SNAP_THRESHOLD = 20;
  const typeColors = getContext("typeColors");
  const dt = new DimText();

  // Necessary data for vertex-dragging
  let tempItem;
  let dragStart = null;
  let dragging = false;

  // Necessary data for vertex-adding
  let tempPt = null;
  let addingPt = null;

  // Necessary data for gridline-adding
  let tempGridline = null;

  // Necessary data for feature-adding
  let currentFeature = null;

  let refreshKey = crypto.randomUUID();
  let previd = itemid;
  let uploadedDrawing;
  let prevUploadedDrawing = uploadedDrawing;

  let cursor = "default";
  let selectStart = null;
  let selecting = null;
  let currentHb = null;
  let tempClip = null;

  $: updateTempItem(item);
  $: locations = containedLocations(item, $group).map((locId) => {
    const loc = $group.locations[locId];
    const item = $group.items[loc.record_id];
    const labelBy = item.is_collection
      ? $group.data.settings.collections_label_by
      : $group.data.settings.label_by;
    const mark = labelBy ? get(item, labelBy) : item.mark;
    return {
      location: loc,
      mark,
      item,
    };
  });
  $: itemsNotInCollection = locations.filter((l) => l?.item?.collection_id !== item.id);
  $: polyface = tempItem && new Polyface(tempItem, $group.data.fabrications);
  $: cornerFabs = polyface?.cornerFabs;
  $: productProject = $group.project_type === "product";
  $: ds = dimSettings($group.data.settings);
  $: sddp = $group.data.settings.decimal_precision;
  $: mmp = $group.data.settings.mm_precision;
  $: precision = ds.displayUnit === "millimeters" ? mmp : sddp;
  $: getUploadedDrawing(item);
  $: refresh(itemid, previd, uploadedDrawing, prevUploadedDrawing);
  $: typeColor = $typeColors[item.type_id];
  $: selectedFeatureObj = makeFeatureObj(selectedFeatures);
  $: hoveredFeatureObj = makeFeatureObj(hoveredFeatures);
  $: highlights = tempItem && getHighlights(polyface, selectedFeatureObj, hoveredFeatureObj);
  $: dwgOptions = {
    ...ds,
    typeColor,
    highlights,
    drawingSelected: !isEmpty(selectedFeatureObj.dwgframe),
    tempClip: tempClip,
    underlayOpacity: $group.data.settings.underlay_opacity,
    tempPt: tempPt && tempPt.pt,
    tempGridline: tempGridline,
    showSpanningDims: $settingDisplayShowSpanningDims,
    showTickDims: true,
    showDrawing,
    labelBy: isCollection ? $group.data.settings.collections_label_by : $group.data.settings.label_by,
    currentFeature,
    drawing: uploadedDrawing,
    interactive: !disabled && !productProject,
    isCollection,
    locations,
    selecting: !dragging && selecting,
    currentTool: $currentTool,
  };
  $: drawing = tempItem && liteDrawing(tempItem, type, $group.data.fabrications, dwgOptions);

  function updateTempItem(item) {
    if (!item) return;
    tempItem = cloneDeep(item);
    tempItem.shape = freeShape(tempItem);
  }

  async function getUploadedDrawing(item) {
    if (!showDrawing) return;
    prevUploadedDrawing = uploadedDrawing;
    if (!item.drawing) {
      uploadedDrawing = null;
      return;
    }

    const url = getDrawingSrc(item.drawing, { full: true });
    if (!url) {
      uploadedDrawing = null;
      return;
    }

    try {
      const img = await loadImage(url);
      uploadedDrawing = {
        image: img,
        width: img.width,
        height: img.height,
      };
    } catch (e) {
      uploadedDrawing = null;
    }
  }

  function drawingsChanged(a, b) {
    if (!a && !b) return false;
    if (a && !b) return true;
    if (!a && b) return true;
    return false;
  }

  function refresh(itemid, previd, ud, pud) {
    if (itemid !== previd || drawingsChanged(ud, pud)) {
      refreshKey = crypto.randomUUID();
    }
  }

  function slideAnimation(node, options) {
    if (slide === "none") return null;

    if (options.type === "in") {
      return fly(node, {
        x: slide === "right" ? -50 : 50,
        delay: 100,
        easing: linear,
        duration: 100,
      });
    }

    return fly(node, {
      x: slide === "right" ? 50 : -50,
      easing: linear,
      duration: 100,
    });
  }

  function makeFeatureObj(featureList) {
    return featureList.reduce(
      (obj, feat) => {
        if (["void", "efab", "cfab", "adfab", "bug"].includes(feat.type)) {
          obj[feat.type][feat.index] = feat;
        } else if (feat.type === "refplane") {
          set(obj, `${feat.type}.${feat.class}.${feat.id}`, feat);
        } else if (feat.type === "dwgframe") {
          set(obj, `${feat.type}.${feat.index}`, feat);
        } else if (feat.type === "cellhb") {
          set(obj, `${feat.type}.${feat.row}_${feat.col}`, feat);
        } else {
          set(obj, `${feat.type}.${feat.loop}.${feat.index}`, feat);
        }

        return obj;
      },
      {
        edge: {},
        vertex: {},
        void: {},
        efab: {},
        cfab: {},
        adfab: {},
        bug: {},
        refplane: {},
        dwgframe: {},
        cellhb: {},
      },
    );
  }

  function makeTypeOptionMap(types) {
    return types.reduce((o, t) => {
      o[t.id] = `${t.mark}${t.name && ` - ${t.name}`}`;
      return o;
    }, {});
  }

  function makeTypeIds(types) {
    return types.map((t) => t.id);
  }

  function getHighlights(polyface, sfo, hfo) {
    const edges = polyface.edges;

    const edgeHighlights = edges.map((loop, l) => {
      return loop
        .map((e, index) => index)
        .filter((index) => {
          if (sfo.edge[l]?.[index]) return true;
          if (hfo.edge[l]?.[index]) return true;
          return false;
        });
    });

    const vertexHighlights = polyface.shape.map((loop, l) => {
      return loop
        .map((v, index) => index)
        .filter((index) => {
          if (sfo.vertex[l]?.[index]) return true;
          if (hfo.vertex[l]?.[index]) return true;
          return false;
        });
    });

    const voids = Object.keys({
      ...sfo.void,
      ...hfo.void,
    })
      .map((i) => ({ ...polyface.voids[i], id: i }))
      .filter((i) => polyface.voids[i.id]);

    const efabs = Object.keys({
      ...sfo.efab,
      ...hfo.efab,
    })
      .map((i) => ({ ...polyface.edgeFabrications[i], id: i }))
      .filter((i) => polyface.edgeFabrications[i.id]);

    const cfabs = Object.keys({
      ...sfo.cfab,
      ...hfo.cfab,
    })
      .map((i) => ({ ...polyface.cornerFabrications[i], id: i }))
      .filter((i) => polyface.cornerFabrications[i.id]);

    const adfabs = Object.keys({
      ...sfo.adfab,
      ...hfo.adfab,
    })
      .map((i) => ({ ...polyface.additiveFabrications[i], id: i }))
      .filter((i) => polyface.additiveFabrications[i.id]);

    const refplanes =
      (!isEmpty(sfo.refplane) || !isEmpty(hfo.refplane)) && merge({}, sfo.refplane, hfo.refplane);

    const cellhb = {
      ...sfo.cellhb,
      ...hfo.cellhb,
    };

    const bug = (!isEmpty(sfo.bug) || !isEmpty(hfo.bug)) && polyface.surfaceFabrications[0];

    const dwgframe = !isEmpty(sfo.dwgframe) || !isEmpty(hfo.dwgframe);

    return {
      edges: edgeHighlights,
      vertices: vertexHighlights,
      voids,
      efabs,
      cfabs,
      adfabs,
      refplanes,
      bug,
      dwgframe,
      cellhb,
    };
  }

  function featHitbox(hb) {
    if (typeof hb !== "string") return null;
    if (hb === "shape") return { type: "shape" };
    const feat = hb.match(
      /(edge|vertex|void|efab|cfab|adfab|bug|refplane|dwgframe|dwg-clip)_(box|cross|horgrid|vertgrid|dwg|[0-9]+)_?([0-9]+|[A-Za-z]+)?/,
    );

    if (!feat) return null;

    if (["void", "efab", "cfab", "adfab", "bug"].includes(feat[1])) {
      return {
        type: feat[1],
        index: parseInt(feat[2]),
        id: `${feat[2]}`,
      };
    }

    if (feat[1] === "refplane") {
      return {
        type: "refplane",
        class: feat[2],
        id: feat[3],
      };
    }

    if (feat[1] === "dwgframe") {
      return {
        type: "dwgframe",
        index: parseInt(feat[2]),
        id: `${feat[2]}`,
      };
    }

    if (feat[1] === "dwg-clip") {
      return {
        type: "dwg-clip",
        class: feat[3] === "outline" ? "outline" : "vertex",
        index: feat[3] === "outline" ? "outline" : parseInt(feat[3]),
      };
    }

    return {
      type: feat[1],
      loop: parseInt(feat[2]),
      index: parseInt(feat[3]),
      id: `${feat[2]}_${feat[3]}`,
    };
  }

  function isHighlighted(feature) {
    if (feature.type === "edge") {
      return dwgOptions.highlights.edges[feature.loop]?.includes(feature.index);
    } else if (feature.type === "vertex") {
      return dwgOptions.highlights.vertices[feature.loop]?.includes(feature.index);
    } else if (["void", "efab", "cfab", "adfab", "bug"].includes(feature.type)) {
      return (
        hoveredFeatureObj[feature.type][feature.index] || selectedFeatureObj[feature.type][feature.index]
      );
    } else if (feature.type === "refplane") {
      return hoveredFeatureObj.refplane?.[feature.class]?.[feature.id];
    } else if (feature.type === "dwgframe") {
      return hoveredFeatureObj.dwgframe?.[feature.index] || selectedFeatureObj.dwgframe?.[feature.index];
    }
  }

  // Update feature data to align w/ vertex removal
  function removeEdge(item, vf, pt) {
    const vertices = item.shape.vertices;
    if (item.data.fabrications?.edge_treatment) {
      const flatindex = findFlatIndex(vertices, vf.loop, vf.index);
      item.data.fabrications.edge_treatment.splice(flatindex, 1);
    }

    if (item.data.fabrications?.voids) {
      // Remove voids that were hosted on an edge that will be disappearing
      item.data.fabrications.voids = item.data.fabrications.voids.filter((f) => {
        if (f.reference.edge === vf.index && f.reference.start === "start") return false;
        if (
          f.reference.edge === (vf.index - 1 + vertices[vf.loop].length) % vertices[vf.loop].length &&
          f.reference.start === "end"
        )
          return false;
        return true;
      });

      // Shift void fabrications as necessary
      item.data.fabrications.voids.forEach((f) => {
        if (f.reference.edge >= vf.index) {
          f.reference.edge -= 1;
        }
      });
    }

    if (item.data.fabrications?.edge) {
      // Remove edge fabrications that were hosted on an edge that will be disappearing
      item.data.fabrications.edge = item.data.fabrications.edge.filter((f) => {
        if (f.reference.edge === vf.index && f.reference.start === "start") return false;
        if (
          f.reference.edge === (vf.index - 1 + vertices[vf.loop].length) % vertices[vf.loop].length &&
          f.reference.start === "end"
        )
          return false;
        return true;
      });

      // Shift edge fabrications as necessary
      item.data.fabrications.edge.forEach((f) => {
        if (f.reference.edge >= vf.index) {
          f.reference.edge -= 1;
        }
      });
    }

    item.data.fabrications?.corner?.forEach((f) => {
      if (f.reference.corner > vf.index) {
        f.reference.corner -= 1;
      }
    });
  }

  // Update feature data to align w/ vertex addition
  function addEdge(item, vf, splitPoint) {
    const vertices = item.shape.vertices;

    if (item.data.fabrications?.edge_treatment) {
      const flatindex = findFlatIndex(vertices, vf.loop, vf.index);
      const val = item.data.fabrications.edge_treatment[flatindex];
      item.data.fabrications.edge_treatment.splice(flatindex, 0, val);
    }

    const d = distSq(splitPoint, vertices[vf.loop][vf.index]);
    const edge = {
      start: vertices[vf.loop][vf.index],
      end: vertices[vf.loop][(vf.index + 2) % vertices[vf.loop].length],
    };

    item.data.fabrications?.voids?.forEach((f) => {
      if (f.reference.edge === vf.index) {
        const fpt = edgePoint(edge, f.reference);
        const fd = distSq(fpt, vertices[vf.loop][vf.index]);

        if (fd > d) f.reference.edge += 1;
      } else if (f.reference.edge > vf.index) {
        f.reference.edge += 1;
      }
    });

    item.data.fabrications?.edge?.forEach((f) => {
      if (f.reference.edge === vf.index) {
        const fpt = edgePoint(edge, f.reference);
        const fd = distSq(fpt, vertices[vf.loop][vf.index]);

        if (fd > d) f.reference.edge += 1;
      } else if (f.reference.edge > vf.index) {
        f.reference.edge += 1;
      }
    });

    item.data.fabrications?.corner?.forEach((f) => {
      if (f.reference.corner > vf.index) {
        f.reference.corner += 1;
      }
    });
  }

  function updateSize(i, deleteShape = false) {
    const pf = new Polyface(i, $group.data.fabrications);
    const bbox = pf.bbox;
    const w = bbox.width - item.cache.width_offset_in;
    const h = bbox.height - item.cache.height_offset_in;

    i.width = dt.parse(w.toString()).value;
    i.height = dt.parse(h.toString()).value;

    // If, after dragging points, we have a rectangle, we can
    // delete the vertices and just rely on the width/height
    // values.
    if (isRectangle(i.shape) && deleteShape) {
      i.rectangle_offset = { x: bbox.xmin, y: bbox.ymin };
      i.shape = { type: "rect" };
    }
  }

  function cornerFabConstraint(fabs, loop, loopIndex, prevIndex, nextIndex) {
    if (fabs[loopIndex]) return "both";
    if (fabs[prevIndex] && fabs[nextIndex]) return "both";

    if (fabs[prevIndex]) {
      return loop[prevIndex].x === loop[loopIndex].x ? "vertical" : "horizontal";
    } else if (fabs[nextIndex]) {
      return loop[loopIndex].x === loop[nextIndex].x ? "vertical" : "horizontal";
    }

    return null;
  }

  function selectFeature(type, id, shiftKey, discKey) {
    const match = id.match(/([0-9]+|[A-Za-z]+)_?([0-9]+|[A-Za-z]+)?/);
    let loop;
    let index;
    let row;
    let col;
    let cls;
    if (["void", "efab", "cfab", "adfab", "dwgframe"].includes(type)) {
      index = parseInt(match[1]);
    } else if (type === "cellhb") {
      loop = match[1];
      index = match[2];
      row = parseInt(match[1]);
      col = parseInt(match[2]);
    } else if (type === "refplane") {
      cls = match[1];
      id = match[2];
    } else {
      loop = parseInt(match[1]);
      index = parseInt(match[2]);
    }
    const feat = { type, loop, index, class: cls, id, row, col };

    if (shiftKey || discKey) {
      let isSelected = false;
      if (["void", "efab", "cfab", "adfab", "dwgframe", "bug"].includes(type)) {
        isSelected = selectedFeatureObj[type][index];
      } else if (type === "cellhb") {
        isSelected = selectedFeatureObj[type]?.[`${feat.row}_${feat.col}`];
      } else if (type === "refplane") {
        isSelected = selectedFeatureObj[type][cls]?.[id];
      } else {
        isSelected = selectedFeatureObj[type][loop]?.[index];
      }

      if (isSelected) {
        selectedFeatures = selectedFeatures.filter(
          (f) => f.type === type && !(f.loop === loop && f.index === index && f.class === cls && f.id === id),
        );
        hoveredFeatures = hoveredFeatures.filter(
          (f) => f.type === type && !(f.loop === loop && f.index === index && f.class === cls && f.id === id),
        );
      } else {
        selectedFeatures = [feat, ...selectedFeatures].sort((a, b) => {
          if (a.loop < b.loop) return -1;
          if (a.loop > b.loop) return 1;
          return a.index - b.index;
        });
      }
    } else {
      selectedFeatures = [feat];
    }
  }

  function deselectFeatures() {
    selectedFeatures = [];
    hoveredFeatures = [];
    activeInput = null;
  }

  function stopEditingDim(e) {
    const isFeatureDim = e.detail && e.detail.id && !!e.detail.id.match(/^feature/);

    if (isFeatureDim) {
      activeInput = null;
    } else {
      deselectFeatures();
    }
  }

  function defMouseCursor(tool, type, id, index) {
    if (tool === "merge-cells") {
      if (type === "cellint") {
        return "cell";
      } else {
        return "default";
      }
    }

    if (tool !== "select") return "default";

    if (type === "dwg-clip") {
      if (["1", "3"].includes(index)) {
        return "nesw-resize";
      } else if (["0", "2"].includes(index)) {
        return "nwse-resize";
      } else if (index === "outline") {
        return "move";
      }
    } else if (type === "dwgframe") {
      return "move";
    }

    return "default";
  }

  function hitboxChange(current, hitbox) {
    if (!current && !hitbox) return false;
    if (!current && hitbox) return true;
    if (current && !hitbox) return true;
    if (current && hitbox) {
      return current.name !== hitbox.name;
    }
  }

  function handleVpMouseover(e) {
    const { hitbox, dwgPt } = e.detail;
    if (!hitbox) return;

    const feat = featHitbox(hitbox.name);
    if (!feat) return;

    if ($currentTool === "select") {
      hoveredFeatures = [feat];
    } else if ($currentTool === "add-point") {
      if (feat.type === "edge") {
        const shape = freeShape(item);
        const l = shape.vertices[feat.loop];
        const a = l[feat.index];
        const b = l[(feat.index + 1) % l.length];
        const nearest = nearestPointOnSegment(a, b, dwgPt);
        if (!equal(nearest, a) && !equal(nearest, b)) {
          tempPt = {
            loop: feat.loop,
            edgeIndex: feat.index,
            pt: nearest,
          };
        } else {
          tempPt = null;
        }
      }
    } else if ($currentTool === "remove-point") {
      if (feat.type === "vertex") {
        hoveredFeatures = [feat];
      }
    } else if ($currentTool === "vertical-gridline" || $currentTool === "horizontal-gridline") {
      if (feat.type === "shape") {
        tempGridline = {
          type: $currentTool === "vertical-gridline" ? "vertical" : "horizontal",
          pt: dwgPt,
        };
      } else {
        tempGridline = null;
      }
    }
  }

  function handleVpMousemove(e) {
    const { hitbox, dwgPt } = e.detail;

    if (hitboxChange(currentHb, hitbox)) {
      currentHb = hitbox;
      if (hitbox && !disabled) {
        const [type, id, index] = hitbox.name.split("_");
        cursor = defMouseCursor($currentTool, type, id, index);
      } else {
        cursor = "default";
      }
    }

    if (!hitbox) {
      currentFeature = null;
      tempPt = null;
      return;
    }

    const feat = featHitbox(hitbox.name);
    if (!feat) {
      currentFeature = null;
      tempPt = null;
      return;
    }

    if ($currentTool === "feature-edge-fabrication" && feat.type === "edge") {
      const edges = polyface.edges;
      const edge = edges[feat.loop][feat.index];
      if (edge.end.bulge) {
        currentFeature = null;
      } else {
        const reference = edgeReference(feat.loop, feat.index, edges, dwgPt, precision, ds.displayUnit);
        currentFeature = {
          type: "edge-fabrication",
          fab_id: $tempFeatureEdgeFabrication.fab_id,
          reference,
        };
      }
    } else if ($currentTool === "feature-corner-fabrication") {
      const reference = nearestCornerReference(0, polyface.shape, dwgPt);
      const index = reference.corner;
      const loop = polyface.shape[0];
      const pt = loop[index];
      const prev = loop[(index + loop.length - 1) % loop.length];
      const next = loop[(index + 1) % loop.length];

      if (isPlumb(prev, pt, next) && distSq(dwgPt, pt) < 150 && !cornerFabs[0][index]) {
        currentFeature = {
          type: "corner-fabrication",
          fab_id: $tempFeatureCornerFabrication.fab_id,
          reference,
        };
      } else {
        currentFeature = null;
      }
    } else if ($currentTool === "feature-edge-aligned-fabrication") {
      const reference = nearestEdgeReference(polyface.edges, dwgPt, precision, ds.displayUnit);

      currentFeature = {
        type: "edge-aligned-fabrication",
        fab_id: $tempFeatureEdgeAlignedFab.fab_id,
        reference: {
          type: "edge-aligned",
          edge: reference.edge,
          loop: reference.loop,
        },
      };
    } else if ($currentTool === "feature-hole" && feat.type === "shape") {
      const reference = nearestEdgeOffsetReference(0, polyface.edges, dwgPt, precision, ds.displayUnit);
      if ($tempFeatureHole?.type === "circular-hole") {
        currentFeature = {
          type: $tempFeatureHole.type,
          diameter: $tempFeatureHole.diameter,
          reference,
        };
      } else if ($tempFeatureHole?.type === "rectangular-hole") {
        currentFeature = {
          type: $tempFeatureHole.type,
          width: $tempFeatureHole.width,
          height: $tempFeatureHole.height,
          radius: $tempFeatureHole.radius,
          alignment: $tempFeatureHole.alignment,
          orientation: $tempFeatureHole.orientation,
          reference,
        };
      }
    } else if ($currentTool === "add-point" && feat.type === "edge") {
      const shape = freeShape(item);
      const l = shape.vertices[feat.loop];
      const a = l[feat.index];
      const b = l[(feat.index + 1) % l.length];
      const nearest = nearestPointOnSegment(a, b, dwgPt);
      if (!equal(nearest, a) && !equal(nearest, b)) {
        tempPt = {
          loop: feat.loop,
          edgeIndex: feat.index,
          pt: nearest,
        };
      } else {
        tempPt = null;
      }
    } else if (["vertical-gridline", "horizontal-gridline"].includes($currentTool) && feat.type === "shape") {
      tempGridline = {
        type: $currentTool === "vertical-gridline" ? "vertical" : "horizontal",
        pt: dwgPt,
      };
    } else {
      currentFeature = null;
      tempPt = null;
      tempGridline = null;
    }
  }

  function handleVpMouseout(e) {
    const { hitbox } = e.detail;
    if (!hitbox) return;

    const feat = featHitbox(hitbox.name);
    if (!feat) return;

    if (isHighlighted(feat)) hoveredFeatures = [];

    if ($currentTool === "add-point") {
      if (feat.type === "edge" && tempPt && feat.loop === tempPt.loop && feat.index === tempPt.edgeIndex) {
        tempPt = null;
      }
    } else if ($currentTool === "vertical-gridline" || $currentTool === "horizontal-gridline") {
      if (feat.type === "shape") {
        tempGridline = null;
      }
    }
  }

  function clipVertex(clip, index) {
    if (index == 0) return { x: clip.x, y: clip.y + clip.height };
    if (index == 1) return { x: clip.x + clip.width, y: clip.y + clip.height };
    if (index == 2) return { x: clip.x + clip.width, y: clip.y };
    return { x: clip.x, y: clip.y };
  }

  function handleVpDragstart(e) {
    if (disabled) return;
    if (productProject) return;
    const { hitbox, dwgPt, screenPt } = e.detail;
    const feat = featHitbox(hitbox?.name);

    if ($currentTool === "select" && feat) {
      // drag an existing vertex
      if (feat.type === "vertex") {
        dragStart = {
          ...e.detail,
          type: feat.type,
          from: tempItem.shape.vertices[0][feat.index],
        };
      }

      // drag an edge
      else if (feat.type === "edge") {
        dragStart = {
          ...e.detail,
          type: feat.type,
          from: [
            polyface.shape[feat.loop][feat.index],
            polyface.shape[feat.loop][(feat.index + 1) % polyface.shape[feat.loop].length],
          ],
        };
      }

      // drag a hole
      else if (feat.type === "void") {
        const fab = tempItem.data.fabrications.voids[feat.index];
        const vertices = polyface.shape[0];
        const edge = {
          start: vertices[fab.reference.edge],
          end: vertices[(fab.reference.edge + 1) % vertices.length],
        };
        const { center } = edgeOffsetCenter(edge, fab.reference);
        dragStart = {
          ...e.detail,
          type: feat.type,
          from: center,
        };
      }

      // drag an edge fabrication
      else if (feat.type === "efab") {
        const fab = tempItem.data.fabrications.edge[feat.index];
        const vertices = polyface.shape[0];
        const edge = {
          start: vertices[fab.reference.edge],
          end: vertices[(fab.reference.edge + 1) % vertices.length],
        };
        const point = edgePoint(edge, fab.reference);
        dragStart = {
          ...e.detail,
          type: feat.type,
          from: point,
        };
      }

      // drag a corner fabrication
      else if (feat.type === "cfab") {
        dragStart = {
          ...e.detail,
          type: feat.type,
        };
      }

      // drag an additive fabrication
      else if (feat.type === "adfab") {
        const fab = tempItem.data.fabrications.additive[feat.index];
        const vertices = polyface.shape[0];
        const edge = {
          start: vertices[fab.reference.edge],
          end: vertices[(fab.reference.edge + 1) % vertices.length],
        };
        const point = midpoint(edge.start, edge.end);
        dragStart = {
          ...e.detail,
          type: feat.type,
          from: point,
        };
      }

      // drag a reference plane
      else if (feat.type === "refplane") {
        let vertices;
        let initialPt;
        let sel = [];

        // Handle "box"-style reference planes
        if (tempItem.data.reference_planes.type === "box") {
          const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
          const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
          const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
          const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

          const mx = x1 + (x2 - x1) / 2;
          const my = y1 + (y2 - y1) / 2;

          vertices = [];

          if (feat.id === "top") {
            tempItem.shape.vertices.forEach((loop, li) => {
              loop.forEach((v, vi) => {
                if (v.y >= my) {
                  vertices.push([li, vi]);
                }
              });
            });
          } else if (feat.id === "right") {
            tempItem.shape.vertices.forEach((loop, li) => {
              loop.forEach((v, vi) => {
                if (v.x >= mx) {
                  vertices.push([li, vi]);
                }
              });
            });
          } else if (feat.id === "bottom") {
            tempItem.shape.vertices.forEach((loop, li) => {
              loop.forEach((v, vi) => {
                if (v.y < my) {
                  vertices.push([li, vi]);
                }
              });
            });
          } else if (feat.id === "left") {
            tempItem.shape.vertices.forEach((loop, li) => {
              loop.forEach((v, vi) => {
                if (v.x < mx) {
                  vertices.push([li, vi]);
                }
              });
            });
          }
        } else if (feat.class === "vertgrid") {
          const idx = parseInt(feat.id);
          vertices = gridlinePositions(tempItem.data.reference_planes, "vertical", tempItem.cache.bbox.xmin);
          initialPt = vertices[idx];
          sel = selectedFeatures.filter((f) => f.type === "refplane" && f.class === "vertgrid");
        } else if (feat.class === "horgrid") {
          const idx = parseInt(feat.id);
          vertices = gridlinePositions(
            tempItem.data.reference_planes,
            "horizontal",
            tempItem.cache.bbox.ymin,
          );
          initialPt = vertices[idx];
          sel = selectedFeatures.filter((f) => f.type === "refplane" && f.class === "horgrid");
        }

        dragStart = {
          ...e.detail,
          vertices,
          initialPt,
          type: feat.type,
          selected: sel,
        };
      }

      // drag the bug
      else if (feat.type === "bug") {
        const fab = tempItem.data.fabrications.bug;
        const vertices = polyface.shape[fab.reference.loop];
        const edge = {
          start: vertices[fab.reference.edge],
          end: vertices[(fab.reference.edge + 1) % vertices.length],
        };
        const { center } = edgeOffsetCenter(edge, fab.reference);

        dragStart = {
          ...e.detail,
          type: feat.type,
          from: center,
        };
      }

      // Drag the drawing
      else if (feat.type === "dwgframe") {
        dragStart = {
          ...e.detail,
          type: feat.type,
          from: item.drawing.clip?.drawing_offset || { x: 0, y: 0 },
        };
      }

      // Drag a drawing vertex
      else if (feat.type === "dwg-clip" && feat.class === "vertex") {
        const m = multiplier(item.drawing.clip.drawing_scale);
        const width = (item.drawing.properties.width * m) / 300;
        const height = (item.drawing.properties.height * m) / 300;
        const x = item.drawing.clip.drawing_offset?.x ?? 0;
        const y = item.drawing.clip.drawing_offset?.y ?? 0;
        const clip = { x, y, width, height };
        const vertex = clipVertex(clip, feat.index);

        dragStart = {
          ...e.detail,
          type: "dwg-clip-vertex",
          clip,
          from: vertex,
        };
      }
    }

    // selection marquee
    else if ($currentTool === "select") {
      selectStart = { dwgPt, screenPt };
    }

    // add a new vertex to an edge
    else if ($currentTool === "add-point") {
      tempPt = null;
      if (feat && feat.type === "edge") {
        tempItem.shape = freeShape(tempItem);
        const l = tempItem.shape.vertices[feat.loop];
        const a = l[feat.index];
        const b = l[(feat.index + 1) % l.length];
        const nearest = nearestPointOnSegment(a, b, dwgPt);
        if (!equal(nearest, a) && !equal(nearest, b)) {
          addingPt = {
            edgeIndex: feat.index,
            pt: nearest,
            orig: nearest,
          };

          l.splice(feat.index + 1, 0, nearest);
          addEdge(tempItem, feat, nearest);
          tempItem = tempItem;
        } else {
          addingPt = null;
        }
      }
    }
  }

  function handleVpDrag(e) {
    // drag an existing vertex
    if ($currentTool === "select") {
      if (selectStart && !selecting && !dragging) {
        if (isDrag(selectStart.screenPt, e.detail.screenPt)) {
          selecting = { start: selectStart.dwgPt, end: e.detail.dwgPt };
        }
      } else if (selecting) {
        selecting.end = e.detail.dwgPt;
      }

      if (!dragStart) return;
      const d = distSq(e.detail.screenPt, dragStart.screenPt);
      if (d > 100) dragging = true;

      if (dragging) {
        if (dragStart.type === "vertex") {
          const feat = featHitbox(dragStart.hitbox.name);
          const shape = tempItem.shape;
          const l = shape.vertices[0];
          const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
          const to = add(dragStart.from, diff);

          let snapped;
          const prevIndex = (feat.index - 1 + l.length) % l.length;
          const nextIndex = (feat.index + 1) % l.length;
          const fc = cornerFabConstraint(cornerFabs[0], l, feat.index, prevIndex, nextIndex);

          const hplanes = [];
          const vplanes = [];
          if (tempItem.data.reference_planes?.type === "box") {
            const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
            const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
            const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
            const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

            hplanes.push(y1, y2);
            vplanes.push(x1, x2);
          } else if (tempItem.data.reference_planes?.type === "cross") {
            const hor = tempItem.data.reference_planes.horizontal.value.toNumber("inches");
            const ver = tempItem.data.reference_planes.vertical.value.toNumber("inches");

            hplanes.push(hor);
            vplanes.push(ver);
          }

          if (e.detail.shiftKey) {
            snapped = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, fc, hplanes, vplanes);
          } else {
            snapped = snap(
              to,
              dragStart.from,
              l[prevIndex],
              l[nextIndex],
              SNAP_THRESHOLD / e.detail.scale,
              fc,
              hplanes,
              vplanes,
            );
          }

          // Ensure we don't delete bulge property when dragging
          shape.vertices[0][feat.index] = {
            ...shape.vertices[0][feat.index],
            ...snapped,
          };
          updateItemCache(tempItem, $group);
          tempItem = tempItem;
        } else if (dragStart.type === "edge") {
          const feat = featHitbox(dragStart.hitbox.name);
          const shape = tempItem.shape;
          const l = shape.vertices[feat.loop];
          const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);

          const to = [add(dragStart.from[0], diff), add(dragStart.from[1], diff)];

          const ai = feat.index;
          const pi = (ai - 1 + l.length) % l.length;
          const bi = (ai + 1) % l.length;
          const ni = (ai + 2) % l.length;

          let snapped;
          const fca = cornerFabConstraint(cornerFabs[feat.loop], l, ai, pi, bi);
          const fcb = cornerFabConstraint(cornerFabs[feat.loop], l, bi, ai, ni);

          const hplanes = [];
          const vplanes = [];

          if (tempItem.data.reference_planes?.type === "box") {
            const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
            const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
            const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
            const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

            hplanes.push(y1, y2);
            vplanes.push(x1, x2);
          } else if (tempItem.data.reference_planes?.type === "cross") {
            const hor = tempItem.data.reference_planes.horizontal.value.toNumber("inches");
            const ver = tempItem.data.reference_planes.vertical.value.toNumber("inches");

            hplanes.push(hor);
            vplanes.push(ver);
          }

          if (e.detail.shiftKey) {
            snapped = snapEdgeOrtho(
              to,
              dragStart.from,
              SNAP_THRESHOLD / e.detail.scale,
              fca,
              fcb,
              hplanes,
              vplanes,
            );
          } else {
            snapped = snapEdge(
              to,
              dragStart.from,
              SNAP_THRESHOLD / e.detail.scale,
              fca,
              fcb,
              hplanes,
              vplanes,
            );
          }

          shape.vertices[feat.loop][ai] = {
            ...shape.vertices[feat.loop][ai],
            ...snapped[0],
          };

          shape.vertices[feat.loop][bi] = {
            ...shape.vertices[feat.loop][bi],
            ...snapped[1],
          };
          updateItemCache(tempItem, $group);
          tempItem = tempItem;
        } else if (dragStart.type === "void") {
          const feat = featHitbox(dragStart.hitbox.name);
          const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
          let to = add(dragStart.from, diff);

          if (e.detail.shiftKey) {
            to = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, null);
          }

          if (polyfaceContainsPoint(polyface, to)) {
            const reference = nearestEdgeOffsetReference(0, polyface.edges, to, precision, ds.displayUnit);
            tempItem.data.fabrications.voids[feat.index].reference = reference;
          }

          tempItem = tempItem;
        } else if (dragStart.type === "efab") {
          const feat = featHitbox(dragStart.hitbox.name);
          const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
          const to = add(dragStart.from, diff);

          const reference = nearestEdgeReference(polyface.edges, to, precision, ds.displayUnit);
          tempItem.data.fabrications.edge[feat.index].reference = reference;
          tempItem = tempItem;
        } else if (dragStart.type === "cfab") {
          const feat = featHitbox(dragStart.hitbox.name);
          const reference = nearestCornerReference(0, polyface.shape, e.detail.dwgPt);
          tempItem.data.fabrications.corner[feat.index].reference = reference;
          tempItem = tempItem;
        } else if (dragStart.type === "adfab") {
          const feat = featHitbox(dragStart.hitbox.name);
          const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
          const to = add(dragStart.from, diff);
          const reference = nearestEdgeReference(polyface.edges, to, precision, ds.displayUnit);
          tempItem.data.fabrications.additive[feat.index].reference = {
            type: "edge-aligned",
            edge: reference.edge,
            loop: reference.loop,
          };
          tempItem = tempItem;
        } else if (dragStart.type === "refplane") {
          const feat = featHitbox(dragStart.hitbox.name);

          if (feat.class === "box") {
            const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
            const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
            const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
            const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

            const v = ["left", "right"].includes(feat.id) ? e.detail.dwgPt.x : e.detail.dwgPt.y;

            if (feat.id === "top") {
              const dy = v - y2;
              dragStart.vertices.forEach(([li, vi]) => {
                tempItem.shape.vertices[li][vi].y += dy;
              });
            } else if (feat.id === "right") {
              const dx = v - x2;
              dragStart.vertices.forEach(([li, vi]) => {
                tempItem.shape.vertices[li][vi].x += dx;
              });
            } else if (feat.id === "bottom") {
              const dy = v - y1;
              dragStart.vertices.forEach(([li, vi]) => {
                tempItem.shape.vertices[li][vi].y += dy;
              });
            } else if (feat.id === "left") {
              const dx = v - x1;
              dragStart.vertices.forEach(([li, vi]) => {
                tempItem.shape.vertices[li][vi].x += dx;
              });
            }

            const val = new Dimension(new Quantity(v, "inches"));
            tempItem.data.reference_planes[feat.id].value = val;
          } else if (feat.class === "cross") {
            const v = feat.id === "horizontal" ? e.detail.dwgPt.y : e.detail.dwgPt.x;
            const val = new Dimension(new Quantity(v, "inches"));
            set(tempItem, `data.reference_planes.${feat.id}.value`, val);
            tempItem = tempItem;
          } else if (feat.class === "vertgrid") {
            const v = [...dragStart.vertices];
            const idx = parseInt(feat.id);
            const snaps = tempItem.cache.vertices.map((v) => v.x);
            const newPt = snapLinear(e.detail.dwgPt.x, SNAP_THRESHOLD / e.detail.scale, [v[idx], ...snaps]);
            const orig = v[idx];
            v[idx] = newPt;
            const diff = newPt - orig;
            dragStart.selected.forEach((feat) => {
              const fIdx = parseInt(feat.id);
              if (fIdx !== idx) {
                v[fIdx] += diff;
              }
            });
            v.sort((a, b) => a - b);
            const planes = tempItem.data.reference_planes;
            const lines = positionsToLines(v, "vertical", tempItem.cache.bbox.xmin);
            planes.x_offset = lines.x_offset;
            planes.vertical = lines.vertical;
          } else if (feat.class === "horgrid") {
            const v = [...dragStart.vertices];
            const idx = parseInt(feat.id);
            const snaps = tempItem.cache.vertices.map((v) => v.y);
            const newPt = snapLinear(e.detail.dwgPt.y, SNAP_THRESHOLD / e.detail.scale, [v[idx], ...snaps]);
            const orig = v[idx];
            v[idx] = newPt;
            const diff = newPt - orig;
            dragStart.selected.forEach((feat) => {
              const fIdx = parseInt(feat.id);
              if (fIdx !== idx) {
                v[fIdx] += diff;
              }
            });

            v.sort((a, b) => a - b);
            const planes = tempItem.data.reference_planes;
            const lines = positionsToLines(v, "horizontal", tempItem.cache.bbox.ymin);
            planes.y_offset = lines.y_offset;
            planes.horizontal = lines.horizontal;
          }

          tempItem = tempItem;
        } else if (dragStart.type === "bug") {
          const feat = featHitbox(dragStart.hitbox.name);
          const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
          let to = add(dragStart.from, diff);

          if (e.detail.shiftKey) {
            to = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, null);
          }

          if (polyfaceContainsPoint(polyface, to)) {
            const reference = nearestEdgeOffsetReference(0, polyface.edges, to, precision, ds.displayUnit);
            tempItem.data.fabrications.bug.reference = reference;
          }
        } else if (dragStart.type === "dwgframe") {
          const feat = featHitbox(dragStart.hitbox.name);
          const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
          let to = add(dragStart.from, diff);

          if (e.detail.shiftKey) {
            to = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, null);
          }

          tempItem.drawing.clip.drawing_offset = to;
        } else if (dragStart.type === "dwg-clip-vertex") {
          const feat = featHitbox(dragStart.hitbox.name);
          const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
          let to = add(dragStart.from, diff);

          if (e.detail.shiftKey) {
            to = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, null);
          }

          tempClip = draggedClip(dragStart.clip, feat.index, to);
        }
      }

      // add a new vertex to shape
    } else if ($currentTool === "add-point") {
      if (addingPt) {
        const l = tempItem.shape.vertices[0];
        const prevIndex = addingPt.edgeIndex;
        const currentIndex = (addingPt.edgeIndex + 1) % l.length;
        const nextIndex = (addingPt.edgeIndex + 2) % l.length;
        const a = l[prevIndex];
        const b = l[nextIndex];
        const fc = cornerFabConstraint(cornerFabs[0], l, currentIndex, prevIndex, nextIndex);

        const hplanes = [];
        const vplanes = [];
        if (tempItem.data.reference_planes?.type === "box") {
          const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
          const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
          const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
          const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

          hplanes.push(y1, y2);
          vplanes.push(x1, x2);
        } else if (tempItem.data.reference_planes?.type === "cross") {
          const hor = tempItem.data.reference_planes.horizontal.value.toNumber("inches");
          const ver = tempItem.data.reference_planes.vertical.value.toNumber("inches");

          hplanes.push(hor);
          vplanes.push(ver);
        }

        const snapped = snap(
          e.detail.dwgPt,
          addingPt.orig,
          a,
          b,
          SNAP_THRESHOLD / e.detail.scale,
          fc,
          hplanes,
          vplanes,
        );
        const nearest = nearestPointOnSegment(a, b, e.detail.dwgPt);
        const d = distSq(nearest, e.detail.dwgPt);

        const pt =
          d > SNAP_THRESHOLD / e.detail.scale || equal(nearest, a) || equal(nearest, b) ? snapped : nearest;

        l[addingPt.edgeIndex + 1] = pt;
        addingPt.pt = pt;
        updateItemCache(tempItem, $group);
        tempItem = tempItem;
      }
    }
  }

  function handleVpDragend(e) {
    // Finish dragging a point
    if ($currentTool === "select") {
      if (selecting) {
        const minX = Math.min(selecting.start.x, selecting.end.x);
        const maxX = Math.max(selecting.start.x, selecting.end.x);
        const minY = Math.min(selecting.start.y, selecting.end.y);
        const maxY = Math.max(selecting.start.y, selecting.end.y);

        if (item.data.reference_planes?.type === "grid") {
          const v = gridlinePositions(item.data.reference_planes, "vertical", item.cache.bbox.xmin);
          const h = gridlinePositions(item.data.reference_planes, "horizontal", item.cache.bbox.ymin);

          const vIndices = [];
          const hIndices = [];

          v.forEach((v, i) => {
            if (v >= minX && v <= maxX) {
              vIndices.push(i);
            }
          });

          h.forEach((h, i) => {
            if (h >= minY && h <= maxY) {
              hIndices.push(i);
            }
          });

          selectedFeatures = [
            ...vIndices.map((i) => ({ type: "refplane", class: "vertgrid", id: `${i}` })),
            ...hIndices.map((i) => ({ type: "refplane", class: "horgrid", id: `${i}` })),
          ];
        }
      } else if (dragging && dragStart?.type === "vertex") {
        const feat = featHitbox(dragStart.hitbox.name);
        const l = tempItem.shape.vertices[0];
        const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
        const to = add(dragStart.from, diff);

        let snapped;
        const prevIndex = (feat.index - 1 + l.length) % l.length;
        const nextIndex = (feat.index + 1) % l.length;
        let fc = cornerFabConstraint(cornerFabs[0], l, feat.index, prevIndex, nextIndex);

        const hplanes = [];
        const vplanes = [];

        if (tempItem.data.reference_planes?.type === "box") {
          const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
          const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
          const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
          const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

          hplanes.push(y1, y2);
          vplanes.push(x1, x2);
        } else if (tempItem.data.reference_planes?.type === "cross") {
          const hor = tempItem.data.reference_planes.horizontal.value.toNumber("inches");
          const ver = tempItem.data.reference_planes.vertical.value.toNumber("inches");

          hplanes.push(hor);
          vplanes.push(ver);
        }

        if (e.detail.shiftKey) {
          snapped = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, fc, hplanes, vplanes);
        } else {
          snapped = snap(
            to,
            dragStart.from,
            l[prevIndex],
            l[nextIndex],
            SNAP_THRESHOLD / e.detail.scale,
            fc,
            hplanes,
            vplanes,
          );
        }

        const pt = { x: snapped.x, y: snapped.y };
        if (to.x === snapped.x) {
          const prev = l[prevIndex];
          const next = l[nextIndex];
          if (to.x !== prev.x && to.x !== next.x) {
            pt.x = roundValue(to.x, sddp);
          }
        }

        if (to.y === snapped.y) {
          const prev = l[prevIndex];
          const next = l[nextIndex];
          if (to.y !== prev.y && to.y !== next.y) {
            pt.y = roundValue(to.y, sddp);
          }
        }

        tempItem.shape.vertices[0][feat.index] = {
          ...tempItem.shape.vertices[0][feat.index],
          ...pt,
        };
        updateSize(tempItem);
        const data = cloneDeep(item.data);
        let shape = tempItem.shape;
        let rectangle_offset = tempItem.rectangle_offset;

        if (isRectangle(shape)) {
          const { xmin, ymin } = polyface.bbox;
          rectangle_offset = { x: xmin, y: ymin };
          shape = { type: "rect" };
        }

        group.updateItem(itemid, {
          rectangle_offset,
          width: tempItem.width,
          height: tempItem.height,
          data,
          shape,
        });
      } else if (dragging && dragStart?.type === "edge") {
        const feat = featHitbox(dragStart.hitbox.name);
        const l = tempItem.shape.vertices[feat.loop];
        const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);

        const to = [add(dragStart.from[0], diff), add(dragStart.from[1], diff)];

        const ai = feat.index;
        const pi = (ai - 1 + l.length) % l.length;
        const bi = (ai + 1) % l.length;
        const ni = (ai + 2) % l.length;

        let snapped;
        const fca = cornerFabConstraint(cornerFabs[feat.loop], l, ai, pi, bi);
        const fcb = cornerFabConstraint(cornerFabs[feat.loop], l, bi, ai, ni);

        const hplanes = [];
        const vplanes = [];

        if (tempItem.data.reference_planes?.type === "box") {
          const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
          const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
          const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
          const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

          hplanes.push(y1, y2);
          vplanes.push(x1, x2);
        } else if (tempItem.data.reference_planes?.type === "cross") {
          const hor = tempItem.data.reference_planes.horizontal.value.toNumber("inches");
          const ver = tempItem.data.reference_planes.vertical.value.toNumber("inches");

          hplanes.push(hor);
          vplanes.push(ver);
        }

        if (e.detail.shiftKey) {
          snapped = snapEdgeOrtho(
            to,
            dragStart.from,
            SNAP_THRESHOLD / e.detail.scale,
            fca,
            fcb,
            hplanes,
            vplanes,
          );
        } else {
          snapped = snapEdge(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, fca, fcb, hplanes, vplanes);
        }

        tempItem.shape.vertices[feat.loop][ai] = {
          ...tempItem.shape.vertices[feat.loop][ai],
          ...snapped[0],
        };

        tempItem.shape.vertices[feat.loop][bi] = {
          ...tempItem.shape.vertices[feat.loop][bi],
          ...snapped[1],
        };
        updateSize(tempItem);
        const data = cloneDeep(item.data);
        let shape = tempItem.shape;
        let rectangle_offset = tempItem.rectangle_offset;

        if (isRectangle(shape)) {
          const { xmin, ymin } = polyface.bbox;
          rectangle_offset = { x: xmin, y: ymin };
          shape = { type: "rect" };
        }

        group.updateItem(itemid, {
          rectangle_offset,
          width: tempItem.width,
          height: tempItem.height,
          data,
          shape,
        });
      } else if (dragging && dragStart?.type === "void") {
        const feat = featHitbox(dragStart.hitbox.name);
        const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
        let to = add(dragStart.from, diff);

        if (e.detail.shiftKey) {
          to = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, null);
        }

        if (polyfaceContainsPoint(polyface, to)) {
          const reference = nearestEdgeOffsetReference(0, polyface.edges, to, precision, ds.displayUnit);
          const data = cloneDeep(item.data);
          data.fabrications.voids[feat.index].reference = reference;
          group.updateItem(itemid, { data });
        }
      } else if (dragging && dragStart?.type === "efab") {
        const feat = featHitbox(dragStart.hitbox.name);
        const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
        const to = add(dragStart.from, diff);

        const reference = nearestEdgeReference(polyface.edges, to, precision, ds.displayUnit);
        const data = cloneDeep(item.data);
        data.fabrications.edge[feat.index].reference = reference;
        group.updateItem(itemid, { data });
      } else if (dragging && dragStart?.type === "cfab") {
        const feat = featHitbox(dragStart.hitbox.name);

        const reference = nearestCornerReference(0, polyface.shape, e.detail.dwgPt);
        const data = cloneDeep(item.data);
        data.fabrications.corner[feat.index].reference = reference;
        group.updateItem(itemid, { data });
      } else if (dragging && dragStart?.type === "adfab") {
        const feat = featHitbox(dragStart.hitbox.name);
        const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
        const to = add(dragStart.from, diff);

        const reference = nearestEdgeReference(polyface.edges, to, precision, ds.displayUnit);
        const data = cloneDeep(item.data);
        data.fabrications.additive[feat.index].reference = {
          type: "edge-aligned",
          edge: reference.edge,
          loop: reference.loop,
        };
        group.updateItem(itemid, { data });
      } else if (dragging && dragStart?.type === "refplane") {
        const feat = featHitbox(dragStart.hitbox.name);

        if (feat.class === "box") {
          const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
          const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
          const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
          const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

          const v = ["left", "right"].includes(feat.id) ? e.detail.dwgPt.x : e.detail.dwgPt.y;

          const vc = ds.displayUnit === "millimeters" ? v * 25.4 : v;
          const rounded = roundValue(vc, precision);
          const rin = ds.displayUnit === "millimeters" ? rounded / 25.4 : rounded;

          if (feat.id === "top") {
            const dy = rin - y2;
            dragStart.vertices.forEach(([li, vi]) => {
              tempItem.shape.vertices[li][vi].y += dy;
            });
          } else if (feat.id === "right") {
            const dx = rin - x2;
            dragStart.vertices.forEach(([li, vi]) => {
              tempItem.shape.vertices[li][vi].x += dx;
            });
          } else if (feat.id === "bottom") {
            const dy = rin - y1;
            dragStart.vertices.forEach(([li, vi]) => {
              tempItem.shape.vertices[li][vi].y += dy;
            });
          } else if (feat.id === "left") {
            const dx = rin - x1;
            dragStart.vertices.forEach(([li, vi]) => {
              tempItem.shape.vertices[li][vi].x += dx;
            });
          }

          const val = new Dimension(new Quantity(rounded, ds.displayUnit));
          tempItem.data.reference_planes[feat.id].value = val;

          updateSize(tempItem);
          const data = cloneDeep(item.data);
          let shape = tempItem.shape;
          let rectangle_offset = tempItem.rectangle_offset;
          data.reference_planes = tempItem.data.reference_planes;

          if (isRectangle(shape)) {
            const { xmin, ymin } = polyface.bbox;
            rectangle_offset = { x: xmin, y: ymin };
            shape = { type: "rect" };
          }

          group.updateItem(itemid, {
            rectangle_offset,
            width: tempItem.width,
            height: tempItem.height,
            data,
            shape,
          });
        } else if (feat.class === "cross") {
          const v = feat.id === "horizontal" ? e.detail.dwgPt.y : e.detail.dwgPt.x;
          const vc = ds.displayUnit === "millimeters" ? v * 25.4 : v;
          const rounded = roundValue(vc, precision);
          const val = new Dimension(new Quantity(rounded, ds.displayUnit));
          item.data.reference_planes[feat.id].value = val;
          group.updateItem(itemid, { data: item.data });
        } else if (feat.class === "vertgrid") {
          const v = [...dragStart.vertices];
          const idx = parseInt(feat.id);
          const snaps = tempItem.cache.vertices.map((v) => v.x);
          const newPt = snapLinear(e.detail.dwgPt.x, SNAP_THRESHOLD / e.detail.scale, [v[idx], ...snaps]);
          const orig = v[idx];
          v[idx] = newPt;
          const diff = newPt - orig;
          dragStart.selected.forEach((feat) => {
            const fIdx = parseInt(feat.id);
            if (fIdx !== idx) {
              v[fIdx] += diff;
            }
          });
          v.sort((a, b) => a - b);
          const planes = cloneDeep(item.data.reference_planes);
          const lines = positionsToLines(v, "vertical", item.cache.bbox.xmin);
          planes.x_offset = lines.x_offset;
          planes.vertical = lines.vertical;
          group.updateItem(itemid, {
            data: {
              ...item.data,
              reference_planes: planes,
            },
          });
        } else if (feat.class === "horgrid") {
          const v = [...dragStart.vertices];
          const idx = parseInt(feat.id);
          const snaps = tempItem.cache.vertices.map((v) => v.y);
          const newPt = snapLinear(e.detail.dwgPt.y, SNAP_THRESHOLD / e.detail.scale, [v[idx], ...snaps]);
          const orig = v[idx];
          v[idx] = newPt;
          const diff = newPt - orig;
          dragStart.selected.forEach((feat) => {
            const fIdx = parseInt(feat.id);
            if (fIdx !== idx) {
              v[fIdx] += diff;
            }
          });

          v.sort((a, b) => a - b);
          const planes = cloneDeep(item.data.reference_planes);
          const lines = positionsToLines(v, "horizontal", item.cache.bbox.ymin);
          planes.y_offset = lines.y_offset;
          planes.horizontal = lines.horizontal;
          group.updateItem(itemid, {
            data: {
              ...item.data,
              reference_planes: planes,
            },
          });
        }
      } else if (dragging && dragStart?.type === "bug") {
        const feat = featHitbox(dragStart.hitbox.name);
        const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
        let to = add(dragStart.from, diff);

        if (e.detail.shiftKey) {
          to = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, null);
        }

        if (polyfaceContainsPoint(polyface, to)) {
          const reference = nearestEdgeOffsetReference(0, polyface.edges, to, precision, ds.displayUnit);
          const data = cloneDeep(item.data);
          data.fabrications.bug.reference = reference;
          group.updateItem(itemid, { data });
        }
      } else if (dragging && dragStart?.type === "dwgframe") {
        const feat = featHitbox(dragStart.hitbox.name);
        const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
        let to = add(dragStart.from, diff);

        if (e.detail.shiftKey) {
          to = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, null);
        }

        const drawing = cloneDeep(item.drawing);
        drawing.clip.drawing_offset = to;
        const updates = [{ id: itemid, type: "item", diff: { drawing } }];

        if (drawing.attachment_id && $group.locations[drawing.attachment_id]?.clip) {
          const clip = cloneDeep($group.locations[drawing.attachment_id].clip);
          clip.drawing_offset = to;
          updates.push({
            id: drawing.attachment_id,
            type: "location",
            diff: {
              clip,
            },
          });
        }

        group.update(updates);
      } else if (dragging && dragStart?.type === "dwg-clip-vertex") {
        const feat = featHitbox(dragStart.hitbox.name);
        const diff = subtract(e.detail.dwgPt, dragStart.dwgPt);
        let to = add(dragStart.from, diff);

        if (e.detail.shiftKey) {
          to = snapOrtho(to, dragStart.from, SNAP_THRESHOLD / e.detail.scale, null);
        }

        const dragged = draggedClip(dragStart.clip, feat.index, to);

        const drawing = cloneDeep(item.drawing);
        const m = multiplier(drawing.clip.drawing_scale);
        const width = Math.round((dragged.width * 300) / m);
        const height = Math.round((dragged.height * 300) / m);
        const cdiff = subtract(dragged, dragStart.clip);
        const scdiff = scale(cdiff, 300 / m);
        const hdiff = height - drawing.properties.height;
        const o = add(drawing.clip, { x: scdiff.x, y: -scdiff.y - hdiff });
        const origDoff = {
          x: drawing.clip.drawing_offset?.x || 0,
          y: drawing.clip.drawing_offset?.y || 0,
        };
        const drawing_offset = add(origDoff, cdiff);
        const doff = { x: drawing_offset.x, y: drawing_offset.y };

        drawing.clip = { ...drawing.clip, x: o.x, y: o.y, width, height, drawing_offset: doff };
        drawing.properties.width = width;
        drawing.properties.height = height;

        const updates = [{ id: itemid, type: "item", diff: { drawing } }];
        const loc = $group.locations[drawing.attachment_id];
        if (loc) {
          updates.push({ id: loc.id, type: "location", diff: { clip: drawing.clip } });
        }

        group.update(updates);
        tempClip = null;
      }

      // Finish adding a new point
    } else if ($currentTool === "add-point") {
      if (addingPt) {
        const l = tempItem.shape.vertices[0];
        const prevIndex = addingPt.edgeIndex;
        const currentIndex = (addingPt.edgeIndex + 1) % l.length;
        const nextIndex = (addingPt.edgeIndex + 2) % l.length;
        const a = l[prevIndex];
        const b = l[nextIndex];
        const fc = cornerFabConstraint(cornerFabs[0], l, currentIndex, prevIndex, nextIndex);

        const m = roundPt(e.detail.dwgPt, sddp);

        const hplanes = [];
        const vplanes = [];
        if (tempItem.data.reference_planes?.type === "box") {
          const x1 = tempItem.data.reference_planes.left.value.toNumber("inches");
          const x2 = tempItem.data.reference_planes.right.value.toNumber("inches");
          const y1 = tempItem.data.reference_planes.bottom.value.toNumber("inches");
          const y2 = tempItem.data.reference_planes.top.value.toNumber("inches");

          hplanes.push(y1, y2);
          vplanes.push(x1, x2);
        } else if (tempItem.data.reference_planes?.type === "cross") {
          const hor = tempItem.data.reference_planes.horizontal.value.toNumber("inches");
          const ver = tempItem.data.reference_planes.vertical.value.toNumber("inches");

          hplanes.push(hor);
          vplanes.push(ver);
        }

        const snapped = snap(m, addingPt.orig, a, b, SNAP_THRESHOLD / e.detail.scale, fc, hplanes, vplanes);
        const nearest = nearestPointOnSegment(a, b, e.detail.dwgPt);
        const d = distSq(nearest, e.detail.dwgPt);
        const pt =
          d > SNAP_THRESHOLD / e.detail.scale || equal(nearest, a) || equal(nearest, b) ? snapped : nearest;

        l[addingPt.edgeIndex + 1] = pt;
        updateSize(tempItem);
        const data = cloneDeep(item.data);
        const shape = tempItem.shape;
        data.fabrications = tempItem.data.fabrications;
        group.updateItem(itemid, {
          width: tempItem.width,
          height: tempItem.height,
          data,
          shape,
        });
      }

      // Finish adding a vertical gridline
    } else if ($currentTool === "vertical-gridline") {
      if (e.detail.hitbox?.name === "shape" && item.data?.reference_planes?.type === "grid") {
        const data = cloneDeep(item.data);
        const v = gridlinePositions(data.reference_planes, "vertical", item.cache.bbox.xmin);
        const merges = data.reference_planes.merges || [];
        const iIdx = insertionIndex(v, e.detail.dwgPt.x);

        merges.forEach((merge) => {
          const isRight = iIdx <= merge.col;
          const isSpanning = iIdx > merge.col && merge.col + merge.colspan >= iIdx;
          if (isSpanning) {
            merge.colspan += 1;
          } else if (isRight) {
            merge.col += 1;
          }
        });

        v.push(e.detail.dwgPt.x);
        v.sort((a, b) => a - b);
        const lines = positionsToLines(v, "vertical", item.cache.bbox.xmin);
        data.reference_planes.x_offset = lines.x_offset;
        data.reference_planes.vertical = lines.vertical;
        data.reference_planes.merges = merges;
        group.updateItem(itemid, { data, x_subdivisions: lines.vertical.length });
      }

      // Finish adding a horizontal gridline
    } else if ($currentTool === "horizontal-gridline") {
      if (e.detail.hitbox?.name === "shape" && item.data?.reference_planes?.type === "grid") {
        const data = cloneDeep(item.data);
        const v = gridlinePositions(data.reference_planes, "horizontal", item.cache.bbox.ymin);
        const merges = data.reference_planes.merges || [];
        const iIdx = insertionIndex(v, e.detail.dwgPt.y);

        merges.forEach((merge) => {
          const mIdx = v.length - merge.row;
          const isBelow = iIdx >= mIdx;
          const isSpanning = iIdx < mIdx && mIdx - merge.rowspan <= iIdx;

          if (isSpanning) {
            merge.rowspan += 1;
          } else if (isBelow) {
            merge.row += 1;
          }
        });

        v.push(e.detail.dwgPt.y);
        v.sort((a, b) => a - b);
        const lines = positionsToLines(v, "horizontal", item.cache.bbox.ymin);
        data.reference_planes.y_offset = lines.y_offset;
        data.reference_planes.horizontal = lines.horizontal;
        data.reference_planes.merges = merges;
        group.updateItem(itemid, { data, y_subdivisions: lines.horizontal.length });
      }
    }

    dragStart = null;
    addingPt = null;
  }

  function isDrag(startPt, endPt) {
    if (distSq(startPt, endPt) > SNAP_THRESHOLD) {
      return true;
    }

    return false;
  }

  async function handleVpClick(e) {
    const { hitbox: hb, shiftKey, pt, dim } = e.detail;
    const dk = discontiguousKey(e.detail);

    // handle feature selection
    if ($currentTool === "select") {
      if (selecting) {
        selectStart = null;
        selecting = null;
        return;
      } else if (dragging) {
        dragging = false;
        return;
      } else if (!hb) {
        return deselectFeatures();
      }
      await tick();

      const hbMatch = hb.name.match(
        /^(edge|vertex|dim|dimonly|dtdim|mark|type|void|efab|cfab|adfab|bug|refplane|dwgframe|dwg-clip|cellhb)_([^|]+)\|?(.+)?$/,
      );
      if (!hbMatch) return deselectFeatures();
      const hitbox = hb.name;
      const type = hbMatch[1];
      const id = hbMatch[2];
      const path = hbMatch[3];

      switch (type) {
        case "vertex":
        case "edge":
        case "void":
        case "efab":
        case "cfab":
        case "adfab":
        case "bug":
        case "dwgframe":
        case "refplane":
        case "cellhb":
          selectFeature(type, id, shiftKey, dk);
          break;
        case "dtdim":
        case "dimonly":
        case "dim":
          activeInput = { hitbox, id, pt, type, dim, path };
          break;
        case "type":
        case "mark":
          activeInput = { hitbox, id, pt, type };
          break;
        case "dwg-clip":
          selectFeature("dwgframe", "0", shiftKey, dk);
          break;
      }

      // handle point removal
    } else if ($currentTool === "remove-point") {
      if (!hb) return;
      deselectFeatures();
      await tick();
      const feat = featHitbox(hb.name);
      if (feat?.type !== "vertex") return;
      const i = cloneDeep(item);
      const shape = freeShape(i);
      i.shape = shape;
      if (shape.vertices[feat.loop].length <= 3) return;

      // Don't allow point removal if it will make an adjacent corner fab out-of-plumb
      const loop = shape.vertices[feat.loop];
      const prevIndex = (feat.index + loop.length - 1) % loop.length;
      const nextIndex = (feat.index + 1) % loop.length;
      if (adjacentVertexConstrained(cornerFabs[feat.loop], loop, feat.index, prevIndex, nextIndex)) return;

      const pt = loop[feat.index];
      shape.vertices[feat.loop] = shape.vertices[feat.loop].filter((v, i) => feat.index !== i);
      removeEdge(i, feat, pt);
      updateSize(i, true);

      group.updateItem(itemid, {
        rectangle_offset: i.rectangle_offset,
        shape: i.shape,
        width: i.width,
        height: i.height,
        data: i.data,
      });

      // handle edge fabrication addition
    } else if ($currentTool === "feature-edge-fabrication" && currentFeature) {
      const data = cloneDeep(item.data);
      if (!data?.fabrications?.edge) {
        set(data, "fabrications.edge", []);
      }
      data.fabrications.edge.push(currentFeature);
      group.updateItem(itemid, { data });

      // handle corner fabrication addition
    } else if ($currentTool === "feature-edge-aligned-fabrication" && currentFeature) {
      const data = cloneDeep(item.data);
      const fab = $group.data.fabrications?.[currentFeature.fab_id];
      if (fab.effect === "add") {
        if (!data?.fabrications?.additive) {
          set(data, "fabrications.additive", []);
        }
        data.fabrications.additive.push(currentFeature);
      }
      group.updateItem(itemid, { data });
    } else if ($currentTool === "feature-corner-fabrication" && currentFeature) {
      const data = cloneDeep(item.data);
      if (!data?.fabrications?.corner) {
        set(data, "fabrications.corner", []);
      }
      data.fabrications.corner.push(currentFeature);
      group.updateItem(itemid, { data });

      // handle hole addition
    } else if ($currentTool === "feature-hole" && currentFeature) {
      const data = cloneDeep(item.data);
      if (!data?.fabrications?.voids) {
        set(data, "fabrications.voids", []);
      }
      data.fabrications.voids.push(currentFeature);
      group.updateItem(itemid, { data });

      // handle cell merges
    } else if ($currentTool === "merge-cells") {
      if (!hb) return deselectFeatures();
      await tick();

      const hbMatch = hb.name.match(/^(cellint)_(horizontal|vertical)_(\d+)_(\d+)$/);
      if (!hbMatch) return deselectFeatures();
      const hitbox = hb.name;

      const orientation = hbMatch[2];
      const x = parseInt(hbMatch[3]);
      const y = parseInt(hbMatch[4]);

      const col = x;
      const row = item.y_subdivisions - y;

      const data = cloneDeep(item.data);
      let ms;
      if (!data.reference_planes?.merges) {
        data.reference_planes.merges = [];
        ms = new MergeStore([]);
      } else {
        ms = item.cache.mergeStore;
      }

      if (orientation === "horizontal") {
        if (ms.hasWallAbove(row, col)) {
          ms.mergeAcrossWall(orientation, row, col);
        } else {
          ms.splitAcrossWall(orientation, row, col);
        }
      } else if (orientation === "vertical") {
        if (ms.hasWallLeft(row, col)) {
          ms.mergeAcrossWall(orientation, row, col);
        } else {
          ms.splitAcrossWall(orientation, row, col);
        }
      }

      data.reference_planes.merges = ms.merges;
      group.updateItem(itemid, { data });
    }
  }

  function mergeCells() {
    const selection = selectedFeatures.map(({ row, col }) => ({ row, col }));

    const data = cloneDeep(item.data);
    let ms;
    if (!data.reference_planes?.merges) {
      data.reference_planes.merges = [];
      ms = new MergeStore([]);
    } else {
      ms = item.cache.mergeStore;
    }

    ms.mergeSelection(selection);
    data.reference_planes.merges = ms.merges;
    group.updateItem(itemid, { data });
  }

  function unmergeCells() {
    const selection = selectedFeatures.map(({ row, col }) => ({ row, col }));

    const data = cloneDeep(item.data);
    let ms;
    if (!data.reference_planes?.merges) {
      data.reference_planes.merges = [];
      ms = new MergeStore([]);
    } else {
      ms = item.cache.mergeStore;
    }

    ms.unmergeSelection(selection);
    data.reference_planes.merges = ms.merges;
    group.updateItem(itemid, { data });
  }

  function markVoidCells() {
    const selection = selectedFeatures.map(({ row, col }) => ({ row, col }));

    const data = cloneDeep(item.data);
    let voids = data.reference_planes.voids ?? [];

    const sel = selection.reduce((acc, s) => {
      acc[`${s.row}_${s.col}`] = true;
      return acc;
    }, {});

    const isSomeSelectionVoid = voids.some((v) => sel[`${v.row}_${v.col}`]);
    voids = voids.filter((v) => !sel[`${v.row}_${v.col}`]);
    if (!isSomeSelectionVoid) {
      voids.push(...selection.map((c) => ({ row: c.row, col: c.col })));
    }

    data.reference_planes.voids = voids;
    group.updateItem(itemid, { data });
  }

  function addItemsToOpening() {
    if (!itemsNotInCollection.length) return;

    const ids = uniqBy(itemsNotInCollection, (l) => l?.location?.record_id).map((l) => l.location.record_id);

    group.updateMultipleItems(ids, "collection_id", item.id);
  }

  function deleteGridlines() {
    if (item?.data.reference_planes?.type !== "grid") return;
    const merges = cloneDeep(item.data.reference_planes.merges || []);

    const vrefplanes = selectedFeatures.filter((f) => f.type === "refplane" && f.class === "vertgrid");
    const hrefplanes = selectedFeatures.filter((f) => f.type === "refplane" && f.class === "horgrid");

    if (vrefplanes.length || hrefplanes.length) {
      const data = cloneDeep(item.data);
      const planes = data.reference_planes;

      let x_subdivisions = item.x_subdivisions;
      if (vrefplanes.length) {
        const v = gridlinePositions(planes, "vertical", item.cache.bbox.xmin);
        const toDelete = vrefplanes.map((p) => parseInt(p.id)).sort((a, b) => b - a);
        merges.forEach((merge) => {
          const sIdx = merge.col;
          const eIdx = sIdx + merge.colspan;

          toDelete.forEach((i) => {
            const isRight = sIdx >= i;
            const isSpanning = sIdx < i && eIdx >= i;

            if (isSpanning) {
              merge.colspan -= 1;
            } else if (isRight) {
              merge.col -= 1;
            }

            if (merge.col < 0 || merge.colspan < 1) {
              merge.delete = true;
            }
          });
        });

        toDelete.forEach((i) => v.splice(i, 1));
        const lines = positionsToLines(v, "vertical", item.cache.bbox.xmin);
        planes.x_offset = lines.x_offset;
        planes.vertical = lines.vertical;
        x_subdivisions = lines.vertical.length;
      }

      let y_subdivisions = item.y_subdivisions;
      if (hrefplanes.length) {
        const v = gridlinePositions(planes, "horizontal", item.cache.bbox.ymin);
        const toDelete = hrefplanes.map((p) => parseInt(p.id)).sort((a, b) => b - a);
        merges.forEach((merge) => {
          const sIdx = v.length - merge.row;
          const eIdx = sIdx - merge.rowspan;

          toDelete.forEach((i) => {
            const isBelow = sIdx <= i;
            const isSpanning = sIdx > i && eIdx <= i;

            if (isSpanning) {
              merge.rowspan -= 1;
            } else if (isBelow) {
              merge.row -= 1;
            }

            if (merge.row < 0 || merge.rowspan < 1) {
              merge.delete = true;
            }
          });
        });
        toDelete.forEach((i) => v.splice(i, 1));
        const lines = positionsToLines(v, "horizontal", item.cache.bbox.ymin);
        planes.y_offset = lines.y_offset;
        planes.horizontal = lines.horizontal;
        y_subdivisions = lines.horizontal.length;
      }

      data.reference_planes.merges = merges.filter((m) => !m.delete);

      group.updateItem(item.id, { data, x_subdivisions, y_subdivisions });
    }
    selectedFeatures = [];
  }

  function distributePlanesHorizontal() {
    if (item?.data.reference_planes?.type !== "grid") return;

    const vrefplanes = selectedFeatures.filter((f) => f.type === "refplane" && f.class === "vertgrid");

    if (vrefplanes.length > 2) {
      const data = cloneDeep(item.data);
      const planes = data.reference_planes;
      const v = gridlinePositions(planes, "vertical", item.cache.bbox.xmin);
      const indices = vrefplanes.map((p) => parseInt(p.id)).sort((a, b) => a - b);
      const min = v[indices[0]];
      const max = v[indices[indices.length - 1]];
      const step = (max - min) / (indices.length - 1);
      indices.slice(1, -1).forEach((i, idx) => {
        v[i] = min + step * (idx + 1);
      });
      const lines = positionsToLines(v, "vertical", item.cache.bbox.xmin);
      planes.x_offset = lines.x_offset;
      planes.vertical = lines.vertical;

      group.updateItem(item.id, { data });
    }
  }

  function distributePlanesVertical() {
    if (item?.data.reference_planes?.type !== "grid") return;

    const hrefplanes = selectedFeatures.filter((f) => f.type === "refplane" && f.class === "horgrid");

    if (hrefplanes.length > 2) {
      const data = cloneDeep(item.data);
      const planes = data.reference_planes;
      const v = gridlinePositions(planes, "horizontal", item.cache.bbox.ymin);
      const indices = hrefplanes.map((p) => parseInt(p.id)).sort((a, b) => a - b);
      const min = v[indices[0]];
      const max = v[indices[indices.length - 1]];
      const step = (max - min) / (indices.length - 1);
      indices.slice(1, -1).forEach((i, idx) => {
        v[i] = min + step * (idx + 1);
      });
      const lines = positionsToLines(v, "horizontal", item.cache.bbox.ymin);
      planes.y_offset = lines.y_offset;
      planes.horizontal = lines.horizontal;

      group.updateItem(item.id, { data });
    }
  }

  function itemCentroid(item, pgonExtents, location) {
    const loc = location || $group.locations[item.drawing.attachment_id];
    const clip = item.drawing.clip;
    let m = 1;
    let offset = { x: 0, y: 0 };
    if (clip?.drawing_scale) {
      m = 300 / multiplier(item.drawing.clip.drawing_scale);

      if (clip.drawing_offset) {
        offset = {
          x: -clip.drawing_offset.x * m,
          y: -clip.drawing_offset.y * m,
        };
      }

      const { xmin, ymin, width, height } = pgonExtents;
      const w = width;
      const h = height;

      return {
        x: clip.x + offset.x + (xmin + w / 2) * m,
        y: clip.y + clip.height - (offset.y + (ymin + h / 2) * m),
      };
    } else {
      return loc.position;
    }
  }

  function pgonExtents(vertices) {
    const x = vertices.map((v) => v.x);
    const y = vertices.map((v) => v.y);
    const xmin = Math.min(...x);
    const xmax = Math.max(...x);
    const ymin = Math.min(...y);
    const ymax = Math.max(...y);
    const width = xmax - xmin;
    const height = ymax - ymin;
    return { xmin, xmax, ymin, ymax, width, height };
  }

  function splitOnGridlines() {
    if (item?.data.reference_planes?.type !== "grid") return;

    const hrefplanes = selectedFeatures.filter((f) => f.type === "refplane" && f.class === "horgrid");
    const vrefplanes = selectedFeatures.filter((f) => f.type === "refplane" && f.class === "vertgrid");

    const origPlanes = item.data.reference_planes;
    const vh = gridlinePositions(origPlanes, "horizontal", item.cache.bbox.xmin);
    const vv = gridlinePositions(origPlanes, "vertical", item.cache.bbox.ymin);

    const vertices = item.cache.vertices;
    const cvh = hrefplanes.map((h) => vh[h.id]);
    const cvv = vrefplanes.map((v) => vv[v.id]);

    const slices = slicePolygon(vertices, cvh, cvv);
    const [orig, ...newShapes] = slices;

    const updatedItem = cloneDeep(item);
    updatedItem.shape = freeShape(updatedItem);
    updatedItem.shape.vertices[0] = orig;
    const oPgon = pgonExtents(orig);
    updateSize(updatedItem);
    const polyface = new Polyface(updatedItem, $group.data.fabrications);

    if (isRectangle(updatedItem.shape)) {
      const { xmin, ymin } = polyface.bbox;
      updatedItem.rectangle_offset = { x: xmin, y: ymin };
      updatedItem.shape = { type: "rect" };
    }

    const newItems = newShapes.map((pgon, i) => {
      const record = cloneDeep(updatedItem);
      record.id = crypto.randomUUID();
      record.mark = updatedItem.mark + `.${i + 2}`;
      record.shape = freeShape(record);
      record.shape.vertices[0] = pgon;
      const pface = new Polyface(record, $group.data.fabrications);
      const { xmin, ymin, xmax, ymax } = pface.bbox;
      updateSize(record);
      const hp = vh.filter((v) => v <= ymax && v >= ymin);
      const hlines = positionsToLines(hp, "horizontal", ymin);
      record.data.reference_planes.x_offset = hlines.x_offset;
      record.data.reference_planes.horizontal = hlines.horizontal;
      const vp = vv.filter((v) => v <= xmax && v >= xmin);
      const vlines = positionsToLines(vp, "vertical", xmin);
      record.data.reference_planes.y_offset = vlines.y_offset;
      record.data.reference_planes.vertical = vlines.vertical;

      if (isRectangle(record.shape)) {
        const { xmin, ymin } = pface.bbox;
        record.rectangle_offset = { x: xmin, y: ymin };
        record.shape = { type: "rect" };
      }

      if ($group.locations[updatedItem.drawing?.attachment_id]) {
        record.drawing.attachment_id = crypto.randomUUID();
      }

      return record;
    });

    const { xmin, ymin, xmax, ymax } = polyface.bbox;
    const hp = vh.filter((v) => v <= ymax && v >= ymin);
    const vp = vv.filter((v) => v <= xmax && v >= xmin);
    const hlines = positionsToLines(hp, "horizontal", ymin);
    updatedItem.data.reference_planes.y_offset = hlines.y_offset;
    updatedItem.data.reference_planes.horizontal = hlines.horizontal;
    const vlines = positionsToLines(vp, "vertical", xmin);
    updatedItem.data.reference_planes.x_offset = vlines.x_offset;
    updatedItem.data.reference_planes.vertical = vlines.vertical;

    const updates = [
      {
        type: "item",
        id: updatedItem.id,
        diff: {
          rectangle_offset: updatedItem.rectangle_offset,
          width: updatedItem.width,
          height: updatedItem.height,
          data: updatedItem.data,
          shape: updatedItem.shape,
          mark: `${updatedItem.mark}.1`,
        },
      },
      {
        type: "item",
        action: "add",
        records: newItems,
      },
    ];

    const oldLoc = $group.locations[updatedItem.drawing?.attachment_id];
    if (oldLoc) {
      updates.push({
        type: "location",
        id: oldLoc.id,
        diff: {
          position: itemCentroid(updatedItem, oPgon),
        },
      });
      const newLocations = newItems.map((item, i) => {
        const pface = new Polyface(item, $group.data.fabrications);
        const pgon = pgonExtents(newShapes[i]);
        const loc = cloneDeep(oldLoc);
        loc.record_id = item.id;
        loc.id = item.drawing.attachment_id;
        loc.position = itemCentroid(item, pgon, loc);
        return loc;
      });
      updates.push({
        type: "location",
        action: "add",
        records: newLocations,
      });
    }

    group.update(updates);
  }
</script>

<div
  class="absolute w-full h-full"
  in:slideAnimation={{ type: "in" }}
  out:slideAnimation={{ type: "out" }}
  style="cursor: {cursor};">
  <Viewport
    key={refreshKey}
    {paddingTop}
    {paddingRight}
    {paddingBottom}
    {paddingLeft}
    autofit={false}
    pannable
    zoomable
    drawing={drawing.drawing}
    hitboxDrawing={drawing.hitboxDrawing}
    {initialExtents}
    bind:dimension={activeInput}
    on:vpmouseover={handleVpMouseover}
    on:vpmousemove={handleVpMousemove}
    on:vpmouseout={handleVpMouseout}
    on:vpdragstart={handleVpDragstart}
    on:vpdrag={handleVpDrag}
    on:vpdragend={handleVpDragend}
    on:vpclick={handleVpClick}
    on:vpdblclick
    on:updatebbox />

  {#if activeInput && !disabled}
    {#key activeInput.id}
      {#if activeInput?.type === "type"}
        <SelectInput
          job={group}
          {item}
          props={activeInput}
          optionMap={makeTypeOptionMap(types)}
          options={makeTypeIds(types)}
          on:stopEditing={deselectFeatures} />
      {:else if ["dim", "dtdim", "dimonly"].includes(activeInput?.type)}
        <DimtextInput
          job={group}
          {item}
          settings={$group.data.settings}
          props={activeInput}
          on:stopEditing={stopEditingDim} />
      {:else if activeInput}
        <TextInput job={group} {item} props={activeInput} on:stopEditing={deselectFeatures} />
      {/if}
    {/key}
  {/if}
  {#if itemsNotInCollection.length > 0}
    <div class="absolute right-0 bottom-0 text-xs p-2">
      <button class="btn-text" on:click|stopPropagation={addItemsToOpening}>Add Items to Opening</button>
    </div>
  {/if}
  {#if showActionButtons && selectedFeatures.length > 0}
    {#if selectedFeatures.every((f) => f.type === "refplane")}
      {@const vertPlanes = selectedFeatures.filter((f) => f.class === "vertgrid").length}
      {@const horPlanes = selectedFeatures.filter((f) => f.class === "horgrid").length}
      <SelectedActions
        selected={selectedFeatures.length}
        hordistributable={!disabled && vertPlanes > 2}
        verdistributable={!disabled && horPlanes > 2}
        splittable={!disabled}
        deletable={!disabled}
        on:delete={deleteGridlines}
        on:distribute-horizontal={distributePlanesHorizontal}
        on:distribute-vertical={distributePlanesVertical}
        on:split={splitOnGridlines} />
    {:else if selectedFeatures.every((f) => f.type === "cellhb")}
      <SelectedActions
        selected={selectedFeatures.length}
        mergeable={!disabled}
        unmergeable={!disabled}
        on:merge={mergeCells}
        on:un-merge={unmergeCells}
        on:mark-void={markVoidCells} />
    {/if}
  {/if}
  {#if item.drawing && item.show_drawing_overlay && !item.drawing.clip?.drawing_scale}
    <div class="absolute left-0 top-0 text-xs flex items-center p-2 gap-1 text-red-500">
      <WarningIcon />
      <div>Warning: Clip has undefined scale</div>
    </div>
  {/if}
</div>
